/**
 * SelectionManager - PlayCanvas entity selection for editor
 *
 * Handles raycasting, selection state, and visual feedback.
 */

import * as pc from 'playcanvas';
import { pickDiag } from '../dynamic-viewer/pickDiagnostics';
import { unifiedGpuSplatPickSupported } from '../dynamic-viewer/SplatDepthPicker';
import { linkEntityOpacityClone, unlinkEntityOpacityClone, type TranslucentCapableMaterial } from '../utils/materialOpacity';

/**
 * Layers the entity pick pass renders, by name. pc.Picker only draws mesh instances on the
 * layers it is given, and it draws them in layer-composition order whatever the order here,
 * so occlusion in the pick buffer matches the screen: World and HotspotMeshLayer's opaque
 * meshes first (they write depth), then TranslucentEntityLayer's translucent sub-layer right
 * after the splats (depth-tested, so an opaque mesh in front still wins the pick).
 */
export const ENTITY_PICK_LAYER_NAMES = ['World', 'HotspotMeshLayer', 'TranslucentEntityLayer'] as const;

export interface SelectionEvent {
  entity: pc.Entity | null;
  previousEntity: pc.Entity | null;
}

export interface SelectionManagerConfig {
  highlightColor?: pc.Color;
  multiSelect?: boolean;
  /**
   * The entity a click on `node` (the picked mesh's entity) selects, e.g. the editor's light or
   * portal entity that owns that marker; null falls back to the tag walk below.
   */
  resolveEntity?: (node: pc.Entity) => pc.Entity | null;
  /**
   * Whether a selected entity gets the emissive selection tint. Default: every entity. The editor
   * leaves hotspot icons alone (a flat sprite tinted blue looks broken; the gizmo and the open
   * hotspot panel already show what is selected).
   */
  highlight?: (entity: pc.Entity) => boolean;
}

/** Entities tagged with one of these are selectable as a whole (their own mesh or a child's). */
const SELECTABLE_TAGS = ['selectable', 'hotspot', 'waypoint', 'light', 'custom-mesh'];

/**
 * The entity a click on `node` (a picked mesh's entity) selects: what `resolveEntity` says, else
 * the nearest tagged entity (the picked one included: a light that draws its own sphere sits
 * directly under the scene root, and a parents-only walk would pass it by), else the top-level
 * entity under `root`.
 */
export function selectableEntityFor(
  node: pc.Entity,
  root: pc.GraphNode,
  resolveEntity?: (node: pc.Entity) => pc.Entity | null,
): pc.Entity {
  const resolved = resolveEntity?.(node);
  if (resolved) return resolved;
  for (let e: pc.Entity | null = node; e && e !== root; e = e.parent as pc.Entity | null) {
    if (SELECTABLE_TAGS.some((tag) => e!.tags?.has(tag))) return e;
  }
  let entity = node;
  while (entity.parent && entity.parent !== root) entity = entity.parent as pc.Entity;
  return entity;
}

/**
 * What a Picker hit selects. On 2.21+ the Picker maps ids to MeshInstances OR GSplatComponents.
 * A splat hit means the click landed on the splat "background": keep the established semantics
 * (clicking the splat deselects) rather than selecting the splat entity. The unified gsplat
 * renderer also draws through mesh instances on its own GSplatManager node, which is not part of
 * the scene: a hit there is the splat too, not an entity to select (selecting it detached the
 * gizmo from whatever was being edited).
 */
export function pickedEntityFor(
  hit: unknown,
  root: pc.GraphNode,
  resolveEntity?: (node: pc.Entity) => pc.Entity | null,
): pc.Entity | null {
  if (!hit || hit instanceof pc.GSplatComponent) return null;
  const node = (hit as pc.MeshInstance).node as pc.Entity | undefined;
  if (!node) return null;
  if (node.root !== root && !resolveEntity?.(node)) return null;
  return selectableEntityFor(node, root, resolveEntity);
}

export class SelectionManager {
  private app: pc.Application;
  private camera: pc.Entity;
  private picker: pc.Picker;

  private selectedEntities: Set<pc.Entity> = new Set();
  private highlightedEntity: pc.Entity | null = null;
  private config: SelectionManagerConfig;

  // Original materials storage for highlight restoration
  private originalMaterials: Map<pc.Entity, Map<number, pc.Material>> = new Map();

  // Event callbacks
  private onSelectionChange?: (event: SelectionEvent) => void;

  constructor(
    app: pc.Application,
    camera: pc.Entity,
    config: SelectionManagerConfig = {}
  ) {
    this.app = app;
    this.camera = camera;
    this.config = {
      ...config,
      highlightColor: config.highlightColor ?? new pc.Color(0.2, 0.5, 1, 1),
      multiSelect: config.multiSelect ?? false,
    };

    // Create picker with depth enabled for GSplat support (PlayCanvas 2.14+)
    this.picker = new pc.Picker(app, 1, 1, true);
  }

  /**
   * Layers the entity pick pass renders. pc.Picker only draws mesh instances that
   * live on the layers it is given. World holds the splat and the light / audio
   * helper meshes, but createViewer's loadCustomMesh (and the hotspot / portal
   * builders) move their render components onto the viewer's HotspotMeshLayer so
   * they depth-sort against the gsplat — with World alone, imported GLB models
   * were never in the pick buffer and a viewport click on one could not select
   * it. HotspotMeshLayer sits right after World in the composition with no depth
   * clear, so the pick pass keeps correct occlusion between a model and the splat.
   * Entities below full opacity live on TranslucentEntityLayer instead, so it is picked too
   * (see ENTITY_PICK_LAYER_NAMES for the ordering).
   */
  getPickLayers(worldLayer: pc.Layer): pc.Layer[] {
    const layers = [worldLayer];
    for (const name of ENTITY_PICK_LAYER_NAMES) {
      if (name === 'World') continue;
      const layer = this.app.scene.layers.getLayerByName(name);
      if (layer && !layers.includes(layer)) layers.push(layer);
    }
    return layers;
  }

  /**
   * Prepare the picker over the World + custom-mesh layers, including the UNIFIED
   * gsplat in the pick pass when the engine supports it (2.21+ WebGPU with a
   * GPU-sort renderer — the 2.20 pick-path TDR is fixed upstream,
   * playcanvas/engine#9036). With the splat in the pass, world-point picks land
   * exactly on the visible splat surface. Where unsupported we still run the
   * legacy exclusion below — harmless there (CPU-sorted renderers contribute
   * nothing to the pick buffer anyway), and it keeps this safe if the package is
   * ever paired with an older engine.
   */
  private preparePick(cameraComponent: pc.CameraComponent, worldLayer: pc.Layer): void {
    const layers = this.getPickLayers(worldLayer);
    if (unifiedGpuSplatPickSupported(this.app)) {
      this.picker.prepare(cameraComponent, this.app.scene, layers);
    } else {
      this.withGsplatsExcluded(() => {
        this.picker.prepare(cameraComponent, this.app.scene, layers);
      });
    }
  }

  /**
   * Run `fn` with every gsplat-bearing entity temporarily DISABLED.
   *
   * Legacy path for engines/renderers without unified GPU splat picking. On
   * PlayCanvas 2.20 WebGPU, a picker.prepare over a layer containing the unified
   * gsplat performed a SECOND full GPU sort+project against the same shared
   * buffers as the main view — an intermittent TDR (device-loss) trigger.
   * Disabling detaches the splat from layers (its resource stays valid) and
   * re-enabling right after marks the layers dirty for the next real frame.
   */
  private withGsplatsExcluded<T>(fn: () => T): T {
    const gsplatEntities: pc.Entity[] = [];
    const collect = (node: pc.GraphNode) => {
      const e = node as pc.Entity;
      if (e.enabled && (e as pc.Entity & { gsplat?: unknown }).gsplat) gsplatEntities.push(e);
      for (const child of node.children) collect(child);
    };
    collect(this.app.root);
    gsplatEntities.forEach(e => { e.enabled = false; });
    try {
      return fn();
    } finally {
      gsplatEntities.forEach(e => { e.enabled = true; });
    }
  }

  /**
   * Pick entity at screen coordinates
   */
  async pickAtScreenPosition(x: number, y: number): Promise<pc.Entity | null> {
    const canvas = this.app.graphicsDevice.canvas;
    const cameraComponent = this.camera.camera;

    if (!cameraComponent || !canvas) {
      return null;
    }

    // Resize picker to a small region around the click point
    const pickerScale = 0.25;
    const pickerWidth = Math.max(1, Math.floor(canvas.clientWidth * pickerScale));
    const pickerHeight = Math.max(1, Math.floor(canvas.clientHeight * pickerScale));

    this.picker.resize(pickerWidth, pickerHeight);

    // Get the world layer
    const worldLayer = this.app.scene.layers.getLayerByName('World');
    if (!worldLayer) {
      return null;
    }

    pickDiag.push('selection:prepare', {
      x: Math.round(x), y: Math.round(y),
      splatInPickPass: unifiedGpuSplatPickSupported(this.app),
    });
    this.preparePick(cameraComponent, worldLayer);

    // Scale coordinates to picker size
    const pickerX = Math.floor(x * pickerScale);
    const pickerY = Math.floor(y * pickerScale);

    // PlayCanvas 2.18's sync Picker.getSelection returns [] on WebGPU devices
    // (WebGPU readbacks must be async). Use the async variant so selection
    // works on both WebGL2 and WebGPU.
    const selection = await this.picker.getSelectionAsync(pickerX, pickerY);

    return selection && selection.length > 0
      ? pickedEntityFor(selection[0], this.app.root, this.config.resolveEntity)
      : null;
  }

  /**
   * Get world point at screen coordinates (for double-click focus)
   */
  async getWorldPointAtScreen(x: number, y: number): Promise<pc.Vec3 | null> {
    const canvas = this.app.graphicsDevice.canvas;
    const cameraComponent = this.camera.camera;

    if (!cameraComponent || !canvas) {
      return null;
    }

    const pickerScale = 0.25;
    const pickerWidth = Math.max(1, Math.floor(canvas.clientWidth * pickerScale));
    const pickerHeight = Math.max(1, Math.floor(canvas.clientHeight * pickerScale));

    this.picker.resize(pickerWidth, pickerHeight);

    const worldLayer = this.app.scene.layers.getLayerByName('World');
    if (!worldLayer) {
      return null;
    }

    // With unified GPU picking (2.21+ WebGPU GPU-sort) the gsplat renders into the
    // pick pass, so this returns the exact visible splat surface — otherwise it
    // remains a mesh-depth-only fallback to the viewer's CPU raycastScreenPoint.
    this.preparePick(cameraComponent, worldLayer);

    const pickerX = Math.floor(x * pickerScale);
    const pickerY = Math.floor(y * pickerScale);

    try {
      const worldPoint = await this.picker.getWorldPointAsync(pickerX, pickerY);
      return worldPoint || null;
    } catch (err) {
      return null;
    }
  }

  /**
   * Select an entity
   */
  select(entity: pc.Entity | null, additive: boolean = false): void {
    const previousEntity = this.getSelectedEntity();

    if (!additive) {
      // Clear previous selection silently — don't fire onSelectionChange here,
      // we fire a single event at the end with the new entity. Firing both a
      // deselect (entity=null) and a select causes gizmo toggle/flicker.
      this.selectedEntities.forEach(e => this.removeHighlight(e));
      this.selectedEntities.clear();
    }

    if (entity) {
      this.selectedEntities.add(entity);
      this.applyHighlight(entity);
    }

    this.onSelectionChange?.({
      entity,
      previousEntity
    });
  }

  /**
   * Deselect a specific entity
   */
  deselect(entity: pc.Entity): void {
    if (this.selectedEntities.has(entity)) {
      this.selectedEntities.delete(entity);
      this.removeHighlight(entity);

      this.onSelectionChange?.({
        entity: this.getSelectedEntity(),
        previousEntity: entity
      });
    }
  }

  /**
   * Deselect all entities
   */
  deselectAll(): void {
    const previousEntity = this.getSelectedEntity();

    this.selectedEntities.forEach(entity => {
      this.removeHighlight(entity);
    });
    this.selectedEntities.clear();

    if (previousEntity) {
      this.onSelectionChange?.({
        entity: null,
        previousEntity
      });
    }
  }

  /**
   * Check if an entity is selected
   */
  isSelected(entity: pc.Entity): boolean {
    return this.selectedEntities.has(entity);
  }

  /**
   * Get the first selected entity (for single selection mode)
   */
  getSelectedEntity(): pc.Entity | null {
    const entities = Array.from(this.selectedEntities);
    return entities.length > 0 ? entities[0] : null;
  }

  /**
   * Get all selected entities
   */
  getSelectedEntities(): pc.Entity[] {
    return Array.from(this.selectedEntities);
  }

  /**
   * Apply visual highlight to entity
   */
  private applyHighlight(entity: pc.Entity): void {
    // Store original materials and apply highlight
    if (!entity.render) return;
    if (this.config.highlight && !this.config.highlight(entity)) return;

    const materialMap = new Map<number, pc.Material>();
    entity.render.meshInstances.forEach((mi, index) => {
      if (mi.material) {
        materialMap.set(index, mi.material);

        // Create highlighted material clone
        const highlightMaterial = mi.material.clone();
        if (highlightMaterial instanceof pc.StandardMaterial) {
          // Entity opacity: the clone inherits the source's translucency and mirrors every
          // later opacity edit, so a slider drag while selected shows on the clone and the
          // layer sync (which reads the mesh instance's material) keeps the right layer.
          linkEntityOpacityClone(mi.material as unknown as TranslucentCapableMaterial, highlightMaterial as unknown as TranslucentCapableMaterial);
          highlightMaterial.emissive = this.config.highlightColor!;
          highlightMaterial.emissiveIntensity = 0.3;
          highlightMaterial.update();
        }
        mi.material = highlightMaterial;
      }
    });

    this.originalMaterials.set(entity, materialMap);
  }

  /**
   * Remove visual highlight from entity
   */
  private removeHighlight(entity: pc.Entity): void {
    const materialMap = this.originalMaterials.get(entity);
    if (!materialMap || !entity.render) return;

    entity.render.meshInstances.forEach((mi, index) => {
      const originalMaterial = materialMap.get(index);
      if (originalMaterial) {
        // The original received every opacity write made while highlighted (mirrored), so its
        // opacity and translucency — and therefore the entity's layer — are already current.
        if (mi.material && mi.material !== originalMaterial) {
          unlinkEntityOpacityClone(originalMaterial as unknown as TranslucentCapableMaterial, mi.material as unknown as TranslucentCapableMaterial);
        }
        mi.material = originalMaterial;
      }
    });

    this.originalMaterials.delete(entity);
  }

  /**
   * Set selection change callback
   */
  onSelect(callback: (event: SelectionEvent) => void): void {
    this.onSelectionChange = callback;
  }

  /**
   * Handle pointer down event for selection
   */
  async handlePointerDown(event: MouseEvent | TouchEvent, additive: boolean = false): Promise<pc.Entity | null> {
    let x: number, y: number;

    if (event instanceof MouseEvent) {
      x = event.clientX;
      y = event.clientY;
    } else {
      const touch = event.touches[0];
      x = touch.clientX;
      y = touch.clientY;
    }

    const entity = await this.pickAtScreenPosition(x, y);

    if (entity) {
      this.select(entity, additive);
    } else if (!additive) {
      this.deselectAll();
    }

    return entity;
  }

  /**
   * Destroy the selection manager
   */
  destroy(): void {
    this.deselectAll();
    this.selectedEntities.clear();
    this.originalMaterials.clear();
  }
}
